const removeExcessMozillaAndVersion = /^mozilla\/\d\.\d\W/;
const brandList = Object.freeze(['chrome', 'opera', 'safari', 'edge', 'firefox']);
const unknown = 'Unknown';
const empty = '';

const navigator = typeof window !== 'undefined' ? window.navigator : null;

let userAgentData = navigator?.userAgentData ?? null;
let userAgent = navigator?.userAgent ?? null;

const mobiles = {
  iphone: /iphone/,
  ipad: /ipad|macintosh/,
  android: /android/
};

const desktops = {
  windows: /win/,
  mac: /macintosh/,
  linux: /linux/
};

function getOperatingSystem(ua) {
  const mobileOS = Object.keys(mobiles).find(
    os => mobiles[os].test(ua) && navigator?.maxTouchPoints >= 1
  );
  const desktopOS = Object.keys(desktops).find(os => desktops[os].test(ua));
  return mobileOS ?? desktopOS ?? unknown;
}

// Browser detection constants
const BROWSER_PATTERNS = [
  // Edge (Chromium)
  {
    pattern: /edg\/([0-9.]+)/i,
    name: 'Edge'
  },
  // Legacy Edge
  {
    pattern: /edge\/([0-9.]+)/i,
    name: 'Edge Legacy'
  },
  // Internet Explorer
  {
    pattern: /trident/i,
    name: 'Internet Explorer',
    versionPattern: /rv:([0-9.]+)/i
  },
  {
    pattern: /msie ([0-9.]+)/i,
    name: 'Internet Explorer'
  },
  // Yandex Browser
  {
    pattern: /yabrowser\/([0-9.]+)/i,
    name: 'Yandex'
  },
  // UC Browser
  {
    pattern: /ucbrowser\/([0-9.]+)/i,
    name: 'UC Browser'
  },
  // Opera
  {
    pattern: /opr\/([0-9.]+)/i,
    name: 'Opera'
  },
  // Opera Mini
  {
    pattern: /opera mini\/([0-9.]+)/i,
    name: 'Opera Mini'
  },
  // Samsung Internet
  {
    pattern: /samsungbrowser\/([0-9.]+)/i,
    name: 'Samsung Browser'
  },
  // Brave - Note: Brave is mostly identical to Chrome
  {
    pattern: /brave\/([0-9.]+)/i,
    name: 'Brave'
  },
  // Vivaldi
  {
    pattern: /vivaldi\/([0-9.]+)/i,
    name: 'Vivaldi'
  },
  // Chrome
  {
    pattern: /chrome\/([0-9.]+)/i,
    name: 'Chrome'
  },
  // Firefox
  {
    pattern: /firefox\/([0-9.]+)/i,
    name: 'Firefox'
  },
  // Firefox for iOS
  {
    pattern: /fxios\/([0-9.]+)/i,
    name: 'Firefox'
  },
  // Chrome for iOS
  {
    pattern: /crios\/([0-9.]+)/i,
    name: 'Chrome'
  },
  // Safari
  {
    pattern: /version\/([0-9.]+)\s+safari/i,
    name: 'Safari'
  }
];

function getBrowserEngine(ua) {
  const lowerUA = ua.toLowerCase();

  if (lowerUA.includes('gecko/')) return 'Gecko';
  if (lowerUA.includes('trident/')) return 'Trident';
  if (lowerUA.includes('presto/')) return 'Presto';
  if (lowerUA.includes('webkit/')) return 'WebKit';
  if (lowerUA.includes('blink/')) return 'Blink';

  return 'Unknown';
}

function matchBrowser(ua) {
  const lowerUA = ua.toLowerCase();
  const engine = getBrowserEngine(ua);

  for (const browser of BROWSER_PATTERNS) {
    const match = browser.pattern.exec(lowerUA);
    if (match) {
      // If there is a custom version pattern, use that
      if (browser.versionPattern) {
        const versionMatch = browser.versionPattern.exec(lowerUA);
        return {
          engine,
          name: browser.name,
          version: versionMatch ? versionMatch[1] : ''
        };
      }

      return {
        engine,
        name: browser.name,
        version: match[1] ?? ''
      };
    }
  }

  // Safari fallback check
  if (lowerUA.includes('safari') && !lowerUA.includes('chrome') && !lowerUA.includes('android')) {
    const webkitMatch = /webkit\/([0-9.]+)/i.exec(lowerUA);
    return {
      engine,
      name: 'Safari',
      version: webkitMatch ? webkitMatch[1] : ''
    };
  }

  return {
    engine,
    name: 'Unknown',
    version: ''
  };
}

// Handle mobile browsers
function detectMobileBrowser(ua) {
  const isMobile = /Mobile|Android|iPhone|iPad|iPod/i.test(ua);
  if (!isMobile) return null;

  if (/iPhone|iPad|iPod/i.test(ua)) return 'iOS';
  if (/Android/i.test(ua)) return 'Android';
  return 'Mobile';
}

// Main browser detection function
function getBrowserInfo(ua) {
  if (!ua) return { engine: 'Unknown', name: 'Unknown', version: '' };

  // Check for mobile first
  const mobilePlatform = detectMobileBrowser(ua);
  if (mobilePlatform) {
    return matchBrowser(ua);
  }

  // Desktop browser detection
  return matchBrowser(ua);
}

function getPlatformFromUserAgentData(data) {
  const os = data.platform?.toLowerCase() ?? unknown;
  let platformData = { browser: unknown, version: unknown };

  for (const agentBrand of data.brands ?? []) {
    const agentBrandEntry = agentBrand.brand.toLowerCase();
    const foundBrand = brandList.find(brand => agentBrandEntry.includes(brand));
    if (foundBrand) {
      platformData = { browser: foundBrand, version: agentBrand.version };
      break;
    }
  }

  return { os, ...platformData };
}

export function getPlatform(customUserAgent, customUserAgentData) {
  userAgent = typeof customUserAgent === 'string' ? customUserAgent : userAgent;
  userAgentData = typeof customUserAgentData === 'string' ? customUserAgentData : userAgentData;

  if (userAgent) {
    const ua = userAgent.toLowerCase().replace(removeExcessMozillaAndVersion, empty);
    const os = getOperatingSystem(ua);
    const { browser, version } = getBrowserInfo(ua);
    return { os, browser, version };
  }

  if (userAgentData) {
    return getPlatformFromUserAgentData(userAgentData);
  }

  return {
    os: unknown,
    browser: unknown,
    version: unknown
  };
}
